/**
 * streamingBufferReducer — pure reducer for the live-streaming buffer.
 *
 * Extracted from Chat.jsx so the multi-round state machine (delta → tool →
 * delta → tool → ... → finish) can be tested in isolation, without React
 * lifecycle.
 *
 * State shape (or `null` if no stream is active):
 *
 *   {
 *     text: string,         // tokens of the *current* round
 *     preToolText: string,  // accumulated text *before* the active tool call(s)
 *     toolCalls: [{ name, args, id, label }],   // `label`: castellano resuelto por el backend (#1003 F7)
 *     agentName: string|null, // sticky-on-first
 *     color: string|null,     // sticky-on-first
 *   }
 *
 * Actions: { type: 'DELTA', delta }, { type: 'TOOL_CALL_START', tools, agentName, color },
 *          { type: 'SEED', text, preToolText, toolCalls, agentName, color },
 *          { type: 'CLEAR' }.
 *
 * Race policy: a DELTA arriving when state is null re-initializes the
 * buffer (legitimate first delta of a new turn). Chat.jsx layers a 250ms
 * lockout on top to discard "delta after finish" stragglers.
 */

const EMPTY_STATE = Object.freeze({
  text: '',
  preToolText: '',
  toolCalls: [],
  agentName: null,
  color: null,
  // `roundDone` flips to true on a delta with `done:true` (last token of the
  // current round) and back to false on the next non-done delta. Used by the
  // render to hide the blinking cursor during the ~700-1000ms window between
  // the last delta and the persisted `agent_message` swap-in.
  roundDone: false,
});

/**
 * El tipo canónico del estado vive en `useStreamingBuffer.ts` (`StreamingBuffer`); se
 * referencia aquí vía `import(...)` en JSDoc para no duplicar la forma. Declararlo como
 * `object|null` hacía que TS viese ese tipo impreciso desde los consumidores TS y
 * rompía el build de producción (TS2322 en `useStreamingBuffer.ts`), aunque Jest —que
 * usa babel y no comprueba tipos— pasara en verde.
 *
 * @param {import('./useStreamingBuffer').StreamingBuffer|null} state
 * @param {{type: string, [key: string]: any}} action
 * @returns {import('./useStreamingBuffer').StreamingBuffer|null}
 */
export function streamingBufferReducer(state, action) {
  switch (action?.type) {
    case 'DELTA': {
      const delta = action.delta || '';
      const done = action.done === true;
      if (!state) {
        // First delta of a turn: seed agentName/color from fallback when
        // available (typically supplied by Chat.jsx from agentStatus). The
        // happy path — when stream-tool-call-start arrives later with the
        // canonical metadata — preserves these via sticky-on-first in
        // TOOL_CALL_START, so the fallback only fills the gap.
        return {
          ...EMPTY_STATE,
          text: delta,
          agentName: action.fallbackAgentName ?? null,
          color: action.fallbackColor ?? null,
          roundDone: done,
        };
      }
      if (state.roundDone) {
        // Round-promote: a new delta arriving after the previous round closed
        // (done:true) signals a fresh LLM stream session inside the same turn
        // — typical when the Agent V2 loop re-invokes the LLM after a tool.
        // The fork doesn't surface stream-tool-call-start in that scenario
        // (the Agent consumes the tool_call_chunks before they reach the
        // callback), so we synthesize the same `text → preToolText` promotion
        // that TOOL_CALL_START would have done. Keeps multi-round content
        // legible as ghost text instead of concatenating into garbage.
        const newPreTool = state.text
          ? (state.preToolText
              ? state.preToolText + '\n\n' + state.text
              : state.text)
          : state.preToolText;
        return {
          ...state,
          preToolText: newPreTool,
          text: delta,
          roundDone: done,
        };
      }
      return { ...state, text: state.text + delta, roundDone: done };
    }

    case 'TOOL_CALL_START': {
      const base = state || EMPTY_STATE;
      const newPreTool = base.text
        ? (base.preToolText
            ? base.preToolText + '\n\n' + base.text
            : base.text)
        : base.preToolText;
      return {
        text: '',
        preToolText: newPreTool,
        toolCalls: Array.isArray(action.tools) ? action.tools : [],
        agentName: base.agentName ?? action.agentName ?? null,
        color: base.color ?? action.color ?? null,
        roundDone: false,
      };
    }

    case 'SEED': {
      // Rehidratación (#368 caso 3 / #463): snapshot del buffer del servidor
      // al (re)conectar el socket a un thread con stream vivo. Solo aplica
      // sobre burbuja inexistente — el backend emite el snapshot como PRIMER
      // evento stream-* del socket (síncrono antes de registrar la conexión),
      // así que un estado no-null aquí es una burbuja legítima más nueva y no
      // se pisa. Sin contenido visible no se crea burbuja (evita fantasmas si
      // el buffer del servidor existe pero aún no liberó texto).
      if (state) return state;
      const text = typeof action.text === 'string' ? action.text : '';
      const preToolText = typeof action.preToolText === 'string' ? action.preToolText : '';
      const toolCalls = Array.isArray(action.toolCalls) ? action.toolCalls : [];
      if (!text && !preToolText && toolCalls.length === 0) return state;
      return {
        ...EMPTY_STATE,
        text,
        preToolText,
        toolCalls,
        agentName: action.agentName ?? null,
        color: action.color ?? null,
        roundDone: false,
      };
    }

    case 'CLEAR':
      return null;

    default:
      return state;
  }
}

export const INITIAL_STREAMING_BUFFER = null;
